import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { SupportedLocale } from '@/app/config/app.config';
import {
  detectInitialLocale,
  writeStoredLocale,
} from '@/shared/i18n/locale';
import { getDirection } from '@/shared/i18n/direction';
import {
  I18nContext,
  translate,
  type I18nContextValue,
} from '@/shared/i18n';
import type { MessageKey } from '@/shared/i18n/locales/en';

interface I18nProviderProps {
  children: ReactNode;
}

export function I18nProvider({ children }: I18nProviderProps): ReactNode {
  const [locale, setLocaleState] = useState<SupportedLocale>(() =>
    detectInitialLocale(),
  );

  // Reflect lang + dir onto <html>.
  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    document.documentElement.setAttribute('lang', locale);
    document.documentElement.setAttribute('dir', getDirection(locale));
    // Browser tab title — localized brand + tagline, switches with the language.
    document.title = translate(locale, 'meta.title');
  }, [locale]);

  const setLocale = useCallback((next: SupportedLocale) => {
    setLocaleState(next);
    writeStoredLocale(next);
  }, []);

  const t = useCallback((key: MessageKey): string => translate(locale, key), [locale]);

  const value = useMemo<I18nContextValue>(
    () => ({ locale, setLocale, t }),
    [locale, setLocale, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
